import { CommonModule } from '@angular/common';
import { InjectionToken, Injectable, NgZone, Optional, SkipSelf, EventEmitter, Component, forwardRef, Inject, Input, Output, ViewChild, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@angular/common';

const _c0 = ["target"];
const _c1 = function (a0) { return { "hide": a0 }; };
const RECAPTCHA_OPTION = new InjectionToken('RECAPTCHA_OPTION');

/*
 * Common service shared by all reCaptcha component instances
 * through dependency injection.
 * This service has the task of loading the reCaptcha API once for all.
 * Only the first instance of the component creates the service, subsequent
 * components will use the existing instance.
 *
 * As the language is passed to the <script>, the first component
 * determines the language of all subsequent components. This is a limitation
 * of the present Google API.
 */
class ReCaptchaService {
    constructor(zone) {
        this.scriptLoaded = false;
        this.readySubject = new BehaviorSubject(false);
        /* the callback needs to exist before the API is loaded */
        if (typeof window !== 'undefined') {
            window['reCaptchaOnloadCallback'] = (() => {
                zone.run(this.onloadCallback.bind(this));
            });
        }
    }
    getReady(language) {
        if (!this.scriptLoaded) {
            this.scriptLoaded = true;
            let doc = document.body;
            let script = document.createElement('script');
            script.innerHTML = '';
            script.src = 'https://www.google.com/recaptcha/api.js?onload=reCaptchaOnloadCallback&render=explicit' +
                (language ? '&hl=' + language : '');
            script.async = true;
            script.defer = true;
            doc.appendChild(script);
        }
        return this.readySubject.asObservable();
    }
    execute(options = undefined) {
        const captcha = window.grecaptcha;
        const captchaElement = document.querySelector('.g-recaptcha.invisible');
        return captcha.execute(captchaElement.id, options);
    }
    onloadCallback() {
        this.readySubject.next(true);
    }
}
ReCaptchaService.ɵfac = function ReCaptchaService_Factory(t) { return new (t || ReCaptchaService)(ɵngcc0.ɵɵinject(ɵngcc0.NgZone)); };
ReCaptchaService.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: ReCaptchaService, factory: ReCaptchaService.ɵfac });
ReCaptchaService.ctorParameters = () => [
    { type: NgZone }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ReCaptchaService, [{
        type: Injectable
    }], function () { return [{ type: ɵngcc0.NgZone }]; }, null); })();
/* singleton pattern taken from https://github.com/angular/angular/issues/13854 */
function RECAPTCHA_SERVICE_PROVIDER_FACTORY(ngZone, parentDispatcher) {
    return parentDispatcher || new ReCaptchaService(ngZone);
}
const RECAPTCHA_SERVICE_PROVIDER = {
    provide: ReCaptchaService,
    deps: [NgZone, [new Optional(), new SkipSelf(), ReCaptchaService]],
    useFactory: RECAPTCHA_SERVICE_PROVIDER_FACTORY
};

class ReCaptchaComponent {
    constructor(_zone, _captchaService, option) {
        this._zone = _zone;
        this._captchaService = _captchaService;
        this.option = option;
        this.hide = true;
        this.captchaResponse = new EventEmitter();
        this.captchaExpired = new EventEmitter();
        this.widgetId = null;
        this.ngUnsubscribe = new Subject();
        this.onChange = () => { };
        this.onTouched = () => { };
        option = option || {
            language: 'ru',
            invisible: {
                sitekey: this.sitekey || undefined,
                theme: 'light',
                type: 'image',
                tabindex: 0,
                badge: 'bottomright'
            },
        };
    }
    ngAfterViewInit() {
        this.setWidgetId();
    }
    reset() {
        if (this.widgetId === null) {
            return;
        }
        this.grecaptchaReset();
        this.onChange(null);
    }
    setWidgetId() {
        this._captchaService.getReady(this.language || this.option.language)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((ready) => {
            if (!ready) {
                return;
            }
            this.widgetId = this.render(this.targetRef.nativeElement);
        });
    }
    render(target) {
        return window.grecaptcha.render(target, {
            'sitekey': this.getSiteKey(),
            'badge': this.getBadge(),
            'theme': this.getTheme(),
            'type': this.getType(),
            'tabindex': this.getTabindex(),
            'size': this.size || 'invisible',
            'callback': ((response) => this._zone.run(this.recaptchaCallback.bind(this, response))),
            'expired-callback': (() => this._zone.run(this.recaptchaExpiredCallback.bind(this)))
        });
    }
    execute(options = undefined) {
        if (this.size !== 'invisible') {
            return;
        }
        if (this.widgetId === null) {
            throw new Error('Invalid widgetId');
        }
        return window.grecaptcha.execute(this.widgetId, options);
    }
    getResponse() {
        if (this.widgetId === null) {
            throw new Error('Invalid widgetId');
        }
        return window.grecaptcha.getResponse(this.widgetId);
    }
    writeValue(newValue) {
        /* ignore it */
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    ngOnDestroy() {
        this.grecaptchaReset();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    recaptchaCallback(response) {
        this.onChange(response);
        this.onTouched();
        this.captchaResponse.emit(response);
    }
    recaptchaExpiredCallback() {
        this.onChange(null);
        this.onTouched();
        this.captchaExpired.emit();
    }
    grecaptchaReset() {
        if (this.widgetId != null) {
            this._zone.runOutsideAngular(() => window.grecaptcha.reset(this.widgetId));
        }
    }
    getSiteKey() {
        if (this.sitekey) {
            return this.sitekey;
        }
        if (this.size === 'invisible') {
            return this.option.invisible.sitekey;
        }
        if (this.size === 'normal') {
            return this.option.normal.sitekey;
        }
        throw new Error('Invalid sitekey');
    }
    getBadge() {
        if (this.badge) {
            return this.badge;
        }
        if (this.size === 'invisible') {
            return this.option.invisible.badge;
        }
        if (this.size === 'normal') {
            return this.option.normal.badge;
        }
        return 'bottomright';
    }
    getTheme() {
        if (this.theme) {
            return this.theme;
        }
        if (this.size === 'invisible') {
            return this.option.invisible.theme;
        }
        if (this.size === 'normal') {
            return this.option.normal.theme;
        }
        return 'light';
    }
    getType() {
        if (this.type) {
            return this.type;
        }
        if (this.size === 'invisible') {
            return this.option.invisible.type;
        }
        if (this.size === 'normal') {
            return this.option.normal.type;
        }
        return 'image';
    }
    getTabindex() {
        if (this.tabindex) {
            return this.tabindex;
        }
        if (this.size === 'invisible') {
            return this.option.invisible.tabindex;
        }
        if (this.size === 'normal') {
            return this.option.normal.tabindex;
        }
        return 0;
    }
}
ReCaptchaComponent.ɵfac = function ReCaptchaComponent_Factory(t) { return new (t || ReCaptchaComponent)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.NgZone), ɵngcc0.ɵɵdirectiveInject(ReCaptchaService), ɵngcc0.ɵɵdirectiveInject(RECAPTCHA_OPTION)); };
ReCaptchaComponent.ɵcmp = /*@__PURE__*/ ɵngcc0.ɵɵdefineComponent({ type: ReCaptchaComponent, selectors: [["recaptcha"]], viewQuery: function ReCaptchaComponent_Query(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵviewQuery(_c0, 5);
    } if (rf & 2) {
        let _t;
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.targetRef = _t.first);
    } }, inputs: { hide: "hide", sitekey: "sitekey", size: "size", theme: "theme", type: "type", tabindex: "tabindex", badge: "badge", language: "language" }, outputs: { captchaResponse: "captchaResponse", captchaExpired: "captchaExpired" }, features: [ɵngcc0.ɵɵProvidersFeature([
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => ReCaptchaComponent),
                multi: true
            }
        ])], decls: 2, vars: 6, consts: [[3, "ngClass", "id"], ["target", ""]], template: function ReCaptchaComponent_Template(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵelement(0, "div", 0, 1);
    } if (rf & 2) {
        ɵngcc0.ɵɵclassMap("recaptcha g-recaptcha " + (ctx.size || "invisible"));
        ɵngcc0.ɵɵproperty("ngClass", ɵngcc0.ɵɵpureFunction1(4, _c1, ctx.hide))("id", ctx.widgetId);
    } }, directives: [ɵngcc1.NgClass], styles: [".hide[_ngcontent-%COMP%] {\n            display: none;\n        }"] });
ReCaptchaComponent.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: ReCaptchaComponent, factory: ReCaptchaComponent.ɵfac });
ReCaptchaComponent.ctorParameters = () => [
    { type: NgZone },
    { type: ReCaptchaService },
    { type: undefined, decorators: [{ type: Inject, args: [RECAPTCHA_OPTION,] }] }
];
ReCaptchaComponent.propDecorators = {
    sitekey: [{ type: Input }],
    size: [{ type: Input }],
    theme: [{ type: Input }],
    type: [{ type: Input }],
    tabindex: [{ type: Input }],
    badge: [{ type: Input }],
    language: [{ type: Input }],
    hide: [{ type: Input }],
    captchaResponse: [{ type: Output }],
    captchaExpired: [{ type: Output }],
    targetRef: [{ type: ViewChild, args: ['target',] }]
};
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ReCaptchaComponent, [{
        type: Component,
        args: [{
                selector: 'recaptcha',
                template: `
        <div #target
            [ngClass]="{'hide': hide}"
            [class]="'recaptcha g-recaptcha ' + (size || 'invisible')"
            [id]="widgetId">
        </div>
    `,
                providers: [
                    {
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef(() => ReCaptchaComponent),
                        multi: true
                    }
                ],
                styles: [`
        .hide {
            display: none;
        }
    `]
            }]
    }, {
        type: Injectable
    }], function () { return [{ type: ɵngcc0.NgZone }, { type: ReCaptchaService }, { type: undefined, decorators: [{
                type: Inject,
                args: [RECAPTCHA_OPTION]
            }] }]; }, { hide: [{
            type: Input
        }], captchaResponse: [{
            type: Output
        }], captchaExpired: [{
            type: Output
        }], sitekey: [{
            type: Input
        }], size: [{
            type: Input
        }], theme: [{
            type: Input
        }], type: [{
            type: Input
        }], tabindex: [{
            type: Input
        }], badge: [{
            type: Input
        }], language: [{
            type: Input
        }], targetRef: [{
            type: ViewChild,
            args: ['target']
        }] }); })();

class ReCaptchaModule {
    static forRoot(option = undefined) {
        return {
            ngModule: ReCaptchaModule,
            providers: [
                {
                    provide: RECAPTCHA_OPTION,
                    useValue: option
                }
            ]
        };
    }
}
ReCaptchaModule.ɵfac = function ReCaptchaModule_Factory(t) { return new (t || ReCaptchaModule)(); };
ReCaptchaModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: ReCaptchaModule });
ReCaptchaModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({ providers: [RECAPTCHA_SERVICE_PROVIDER], imports: [[CommonModule]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ReCaptchaModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                declarations: [ReCaptchaComponent],
                exports: [ReCaptchaComponent],
                providers: [RECAPTCHA_SERVICE_PROVIDER]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(ReCaptchaModule, { declarations: function () { return [ReCaptchaComponent]; }, imports: function () { return [CommonModule]; }, exports: function () { return [ReCaptchaComponent]; } }); })();

/**
 * Generated bundle index. Do not edit.
 */

export { RECAPTCHA_OPTION, RECAPTCHA_SERVICE_PROVIDER, RECAPTCHA_SERVICE_PROVIDER_FACTORY, ReCaptchaComponent, ReCaptchaModule, ReCaptchaService };

